import React from 'react';
import type { FormikProps } from 'formik';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { useStyles } from '~/assets/styles';
import CardDialog from '~/components/CardDialog';
import { ClaimContextProvider } from '~/components/ClaimContainer';
import { ContactEntity } from '~/components/Contact';
import ContactTextFieldFormik from '~/components/ContactTextFieldFormik';
import Button from '~/components/core/Atomic/Buttons/Button';
import { ErrorHelperTextFormik } from '~/components/core/Formik/ErrorHelperTextFormik';
import LoadingDialog from '~/components/LoadingDialog';
import useOrganization from '~/components/OrganizationContext';
import RadioButtonFormik from '~/components/RadioButtonFormik';
import useDataFetcher from '~/components/useDataFetcher';
import { useFetchClaim } from '~/hooks/useFetchClaim';
import type { ContactBaseModel } from '~/server_shared/swagger/data-contracts';
import { COMMUNICATION_CHANNEL_DICT } from '~/Types';

import { getAllSearchableContactRoles } from './ContactUtils';

export type CommunicationChannel = keyof typeof COMMUNICATION_CHANNEL_DICT;

interface OrganizationContextType {
  organizationContactRolesDict: Record<string, { name: string; is_non_searchable: boolean }>;
}

interface AttachOtherContactsValues {
  contact_id: number | '';
  contact_attachment_choice: string | number;
}
interface AttachOtherContactsProps {
  onSubmit: (values: AttachOtherContactsValues) => void;
  onClose: () => void;
  claimId: number;
  communicationChannel: CommunicationChannel;
  communicationId: number;
}
export const AttachOtherContacts: React.FC<AttachOtherContactsProps> = ({
  onSubmit,
  onClose,
  claimId,
  communicationChannel,
  communicationId,
}) => {
  const { classes } = useStyles();
  const { organizationContactRolesDict } = useOrganization() as OrganizationContextType;

  const [claim, isLoadingClaim, isErrorClaim, reloadClaim] = useFetchClaim(claimId);

  const {
    isLoading: isLoadingPossibleContacts,
    isError: isErrorPossibleContacts,
    data: possibleContacts = [],
  } = useDataFetcher<ContactBaseModel[]>(
    `/api/v1/communications/${communicationId}/possible_contacts`,
    { params: { claim_id: claimId } },
    !!(claim && !isLoadingClaim && !isErrorClaim)
  );

  // This can happen when the User is not authorized to access the claim. Not `return null`: the caller
  // keeps its "show the dialog" state set, so rendering nothing leaves the button that opened us dead on
  // every later click. The fetchers have already reported the error; this adds the close control.
  if (isErrorPossibleContacts || isErrorClaim) {
    return <LoadingDialog isError onClose={onClose} track="Attach Other Contacts" />;
  }

  if (isLoadingClaim || isLoadingPossibleContacts || !claim) {
    return (
      <LoadingDialog
        isError={!!(isErrorClaim || isErrorPossibleContacts) || (!claim && !isLoadingClaim)}
        track="Attach Other Contacts"
      />
    );
  }

  return (
    <Formik<AttachOtherContactsValues>
      initialValues={{
        contact_id: '',
        contact_attachment_choice: '',
      }}
      validationSchema={Yup.object().shape({
        contact_attachment_choice: Yup.string().required('Required'),
        contact_id: Yup.number().when('contact_attachment_choice', {
          is: 'existing_contact',
          then: Yup.number().required('Required'),
        }),
      })}
      onSubmit={onSubmit}
    >
      {(formikProps: FormikProps<AttachOtherContactsValues>) => {
        const { isSubmitting, handleSubmit, values, setFieldValue } = formikProps;
        const disableActions = isSubmitting;
        const handleAttachmentOptionChange = (contactId: number | '', attachmentOptionValue: string | number) => {
          setFieldValue('contact_id', contactId);
          setFieldValue('contact_attachment_choice', attachmentOptionValue);
        };

        return (
          <CardDialog
            isDialog
            title={`Attach ${COMMUNICATION_CHANNEL_DICT[communicationChannel]} To Contact`}
            onClose={onClose}
            fullWidth
            preventClose={disableActions}
          >
            <ClaimContextProvider claim={claim} refreshData={async () => await reloadClaim()}>
              <div className="flex flex-col justify-center space-y-20">
                {possibleContacts?.map((contact) => (
                  <div key={contact.id} className="inline-flex">
                    <RadioButtonFormik
                      id="contact_attachment_choice"
                      label={<ContactEntity contactId={contact.id} contactDisplayName={contact.full_name} />}
                      size="small"
                      optionValue={contact.id}
                      onChange={() => handleAttachmentOptionChange(contact.id, contact.id)}
                    />
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-12">
                  <div>
                    <RadioButtonFormik
                      id="contact_attachment_choice"
                      label="Attach to other existing contact"
                      size="small"
                      optionValue="existing_contact"
                      onChange={() => handleAttachmentOptionChange('', 'existing_contact')}
                    />
                  </div>
                  <div>
                    {values['contact_attachment_choice'] === 'existing_contact' && (
                      <ContactTextFieldFormik
                        id="contact"
                        label="Contact"
                        disabled={isSubmitting}
                        acceptedRoles={getAllSearchableContactRoles(organizationContactRolesDict)}
                        fullWidth
                        fixedSearchResults
                      />
                    )}
                  </div>
                </div>
                <div>
                  <RadioButtonFormik
                    id="contact_attachment_choice"
                    label="Create a new contact"
                    size="small"
                    optionValue="new_contact"
                    onChange={() => handleAttachmentOptionChange('', 'new_contact')}
                  />
                </div>
              </div>

              <ErrorHelperTextFormik id="contact_attachment_choice" />

              <div className={classes.buttonsContainer}>
                <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
                  Attach
                </Button>
              </div>
            </ClaimContextProvider>
          </CardDialog>
        );
      }}
    </Formik>
  );
};

interface AttachPhysicalMailValues {
  contact_id: number | '';
  contact_full_name: string;
}
interface AttachPhysicalMailProps {
  claimId: number;
  onClose: () => void;
  onUpdate: (values: AttachPhysicalMailValues) => void;
}
export const AttachPhysicalMail: React.FC<AttachPhysicalMailProps> = ({ claimId, onClose, onUpdate }) => {
  const { classes } = useStyles();
  const { organizationContactRolesDict } = useOrganization() as OrganizationContextType;

  const [claim, isLoading, isError, reloadClaim] = useFetchClaim(claimId);

  if (isLoading || isError || !claim) {
    return <LoadingDialog isError={isError || (!claim && !isLoading)} onClose={onClose} track="AttachPhysicalMail" />;
  }

  return (
    <Formik<AttachPhysicalMailValues>
      initialValues={{
        contact_id: '',
        contact_full_name: '',
      }}
      validationSchema={Yup.object().shape({
        contact_id: Yup.number().required('Required'),
      })}
      onSubmit={onUpdate}
    >
      {({ isSubmitting, handleSubmit }) => (
        <CardDialog isDialog title="Choose contact" onClose={onClose} maxWidth="xl" preventClose={isSubmitting}>
          <ClaimContextProvider claim={claim} refreshData={async () => await reloadClaim()}>
            <ContactTextFieldFormik
              id="contact"
              label="Contact"
              disabled={isSubmitting}
              acceptedRoles={getAllSearchableContactRoles(organizationContactRolesDict)}
              fullWidth
              fixedSearchResults
            />
            <div className={classes.buttonsContainer}>
              <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => handleSubmit()}>
                Attach
              </Button>
            </div>
          </ClaimContextProvider>
        </CardDialog>
      )}
    </Formik>
  );
};
